import type { User } from "oidc-client-ts";

import type { RefreshLock, RefreshResult } from "authentication/types";
import { readFromLocalStorage, removeFromLocalStorage, writeToLocalStorage } from "common/utilities/localStorage";
import { logger } from "common/utilities/logger";

/*
Why are we doing this? The FWS/loginradius endpoints will fail if we try to use the same refresh token twice, and in the old setup we would some times attempt multiple refreshes at the same time, and when one of them failed we would log the user out instead of refreshing the token. This here is meant to make sure there's no race condition happening and we silently updates the token even if multiple tabs are open
*/

const DEFAULT_LOCK_TTL_MS = 15_000;
const DEFAULT_WAIT_TIMEOUT_MS = 12_000;
const DEFAULT_POLL_INTERVAL_MS = 200;

type OidcUserLike = { id_token?: string; refresh_token?: string };

type CoordinatedSigninSilentArgs = {
  fetchPermissions: (idToken: string) => Promise<unknown>;
  getCurrentOidcUser: () => OidcUserLike | undefined;
  knownIdToken?: string;
  knownRefreshToken?: string;
  signinSilent: () => Promise<User | null>;
};

type WaitResult = { status: "failed" } | { status: "retry" } | { idToken: string; status: "success" };

export const coordinatedSigninSilent = async ({
  fetchPermissions,
  getCurrentOidcUser,
  knownIdToken,
  knownRefreshToken,
  signinSilent,
}: CoordinatedSigninSilentArgs): Promise<string | undefined> => {
  const tabId = getOrCreateTabId();
  const baseline = { idToken: knownIdToken, refreshToken: knownRefreshToken };

  for (let attempts = 0; attempts < 2; attempts += 1) {
    if (tryAcquireRefreshLock(tabId, DEFAULT_LOCK_TTL_MS)) {
      try {
        removeRefreshResult();

        const user = await signinSilent();
        if (user === null) throw new Error("[coordinatedSigninSilent] OIDC user returned from signinSilent is null");
        if (user.id_token === undefined)
          throw new Error("[coordinatedSigninSilent] OIDC user returned from signinSilent is missing an ID token");

        await fetchPermissions(user.id_token);
        writeRefreshResult({ completedAt: Date.now(), owner: tabId, status: "success" });
        return user.id_token;
      } catch (error) {
        writeRefreshResult({ completedAt: Date.now(), owner: tabId, status: "error" });
        logger.error("[coordinatedSigninSilent] Failed to renew tokens", error);
        return undefined;
      } finally {
        releaseRefreshLock(tabId);
      }
    }

    const currentUser = getCurrentOidcUser();
    if (hasAdvancedToken(currentUser, baseline)) {
      await fetchPermissions(currentUser.id_token);
      return currentUser.id_token;
    }

    const waitResult = await waitForRefreshOutcome({
      baseline,
      getCurrentOidcUser,
      pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
      waitTimeoutMs: DEFAULT_WAIT_TIMEOUT_MS,
    });

    if (waitResult.status === "success") {
      await fetchPermissions(waitResult.idToken);
      return waitResult.idToken;
    }

    if (waitResult.status === "failed") return undefined;
  }

  return undefined;
};

const getOrCreateTabId = () => {
  const existingTabId = sessionStorage.getItem("TAB_ID_KEY");
  if (existingTabId !== null) return existingTabId;

  const tabId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem("TAB_ID_KEY", tabId);
  return tabId;
};

const tryAcquireRefreshLock = (owner: string, lockTtlMs: number) => {
  const now = Date.now();
  const existingLock = readRefreshLock();
  if (existingLock !== undefined && existingLock.expiresAt > now && existingLock.owner !== owner) return false;

  const nextLock: RefreshLock = { expiresAt: now + lockTtlMs, owner };
  writeRefreshLock(nextLock);

  const verifiedLock = readRefreshLock();
  return verifiedLock?.owner === owner && verifiedLock.expiresAt === nextLock.expiresAt;
};

const releaseRefreshLock = (owner: string) => {
  const existingLock = readRefreshLock();
  if (existingLock?.owner === owner) removeRefreshLock();
};

const waitForRefreshOutcome = async ({
  baseline,
  getCurrentOidcUser,
  pollIntervalMs,
  waitTimeoutMs,
}: {
  baseline: { idToken?: string; refreshToken?: string };
  getCurrentOidcUser: () => OidcUserLike | undefined;
  pollIntervalMs: number;
  waitTimeoutMs: number;
}): Promise<WaitResult> => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < waitTimeoutMs) {
    const currentUser = getCurrentOidcUser();
    if (hasAdvancedToken(currentUser, baseline)) return { idToken: currentUser.id_token, status: "success" };

    const refreshResult = readRefreshResult();
    if (refreshResult !== undefined && refreshResult.completedAt >= startedAt) {
      if (refreshResult.status === "error") return { status: "retry" };
      if (currentUser?.id_token !== undefined) return { idToken: currentUser.id_token, status: "success" };
    }

    const refreshLock = readRefreshLock();
    if (refreshLock === undefined || refreshLock.expiresAt <= Date.now()) return { status: "retry" };

    await delay(pollIntervalMs);
  }

  const currentUser = getCurrentOidcUser();
  return hasAdvancedToken(currentUser, baseline)
    ? { idToken: currentUser.id_token, status: "success" }
    : { status: "failed" };
};

const hasAdvancedToken = (
  user: OidcUserLike | undefined,
  baseline: { idToken?: string; refreshToken?: string },
): user is OidcUserLike & { id_token: string } =>
  user?.id_token !== undefined && (user.id_token !== baseline.idToken || user.refresh_token !== baseline.refreshToken);

const readRefreshLock = () => readFromLocalStorage("REFRESH_LOCK_KEY");

const writeRefreshLock = (value: RefreshLock) => writeToLocalStorage("REFRESH_LOCK_KEY", value);

const removeRefreshLock = () => removeFromLocalStorage("REFRESH_LOCK_KEY");

const readRefreshResult = () => readFromLocalStorage("REFRESH_RESULT_KEY");

const writeRefreshResult = (value: RefreshResult) => writeToLocalStorage("REFRESH_RESULT_KEY", value);

const removeRefreshResult = () => removeFromLocalStorage("REFRESH_RESULT_KEY");

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
